import React, { memo, useRef } from "react";

import { AnalyzeTinybirdResourceResponseBodyType } from "@intelligems/ig-types/functions/analytics/analyze-resource-tinybird/analyze-resource-tinybird";
import { GraphConfig } from "@intelligems/ig-types/schemas/analytics/custom-graphs";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import { selectData } from "@src/api/analytics/dashboard-tinybird";
import { useConfiguration } from "@src/api/configurations/use-configuration";
import { useAnalyticsFetching } from "@src/api/analytics/hooks/use-analytics-fetching";
import { BaseMetricSection } from "@src/features/analytics/baseDashboard/BaseMetricSection";
import { BaseGraphPaper } from "@src/features/analytics/baseDashboard/chartComponents/GraphPapers";
import { GraphsWrapper } from "@src/features/analytics/baseDashboard/chartComponents/GraphsWrapper";
import { useSetGridWidthAnalytics } from "@src/features/analytics/hooks/use-set-grid-width-analytics";
import {
  toUpsellProductRows,
  UpsellProductSource,
} from "@src/features/analytics/tables/upsellProducts/upsellProducts";
import { UpsellProductsTable } from "@src/features/analytics/tables/upsellProducts/UpsellProductsTable";
import { VariationProductMetric } from "@src/features/analytics/types";
import { useSelector } from "@src/store";
import { selectedExperience } from "@src/store/slices/experiences";
import { selectedVariations } from "@src/store/slices/variations";
import { UseQueryResult } from "@tanstack/react-query";

interface UpsellAnalyticsPageProps {
  data: UseQueryResult<AnalyzeTinybirdResourceResponseBodyType>[];
  totalExperienceVisitors: number;
  testDuration: number;
  compareVariationId: string;
  // Per-surface config: which tiles to render, which upsell source drives the
  // product breakdown, and the section title / empty-state copy.
  tileConfig: GraphConfig[];
  productsSource: UpsellProductSource;
  metricsSectionTitle: string;
  productsEmptyMessage: string;
}

export const UpsellAnalyticsPage = memo(
  ({
    data,
    totalExperienceVisitors,
    testDuration,
    compareVariationId,
    tileConfig,
    productsSource,
    metricsSectionTitle,
    productsEmptyMessage,
  }: UpsellAnalyticsPageProps) => {
    const experience = useSelector(selectedExperience);
    const variations = useSelector(selectedVariations);
    const storeCurrencyConfig = useConfiguration<{
      code: string;
      locale: string;
    }>("shop_currency");

    const ref = useRef<HTMLDivElement | null>(null);
    const gridWidth = useSetGridWidthAnalytics(ref);

    const isDataLoading = data[0]?.isLoading || data[0]?.isPending;
    const areProductsFetching = useAnalyticsFetching("variation_products");

    const productsMetrics: VariationProductMetric[] =
      data.find((dataSet) => dataSet.data?.datasetId === "variation_products")
        ?.data?.metrics ?? [];

    return (
      <Stack ref={ref} direction="column" spacing={4}>
        <BaseMetricSection groupTitle={metricsSectionTitle} showCompareGroups>
          {tileConfig.map((config, index) => (
            <Grid size={gridWidth} key={config.title[0] ?? index}>
              <BaseGraphPaper>
                <GraphsWrapper
                  config={config}
                  testDuration={testDuration}
                  totalTestVisitors={totalExperienceVisitors}
                  renderLocation={"experiment"}
                  isDataLoading={isDataLoading}
                  data={selectData({
                    dashboardData: data,
                    config,
                    _resource: experience,
                    locale: storeCurrencyConfig?.value?.locale,
                    metricFilterIndex: 0,
                    variations,
                  })}
                  rawData={data}
                  compareVariationId={compareVariationId}
                />
              </BaseGraphPaper>
            </Grid>
          ))}
        </BaseMetricSection>

        <BaseMetricSection
          groupTitle={"Upsell Product Breakdown"}
          tooltip={"Shows stats on each product purchased through this upsell."}
        >
          <UpsellProductsTable
            rows={toUpsellProductRows(
              productsMetrics,
              variations,
              productsSource
            )}
            isLoading={isDataLoading || areProductsFetching > 0}
            emptyMessage={productsEmptyMessage}
          />
        </BaseMetricSection>
      </Stack>
    );
  }
);

UpsellAnalyticsPage.displayName = "UpsellAnalyticsPage";
